import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, ChevronRight, Trash2, Play, X, AlertCircle, Check, RefreshCw, Filter, ChevronDown, Calculator, Eye, Save, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;

/**
 * Configuration Rules Tab - Admin UI for managing pricing rules
 * 
 * Features:
 * - Create/edit rules that map customer options to Sony catalog items
 * - Preview price calculations
 * - Support for conditions (AND-only)
 * - Quantity modes (per_unit, per_pair, fixed)
 */
export default function ConfigurationRulesTab() {
  const [rules, setRules] = useState([]);
  const [sonyItems, setSonyItems] = useState([]);
  const [configurableOptions, setConfigurableOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptionId, setFilterOptionId] = useState('');
  const [selectedRule, setSelectedRule] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // Preview calculator state
  const [previewConfig, setPreviewConfig] = useState({
    format: '12',
    quantity: 100,
    color_mode: 'black',
    vinyl_weight: '140g',
    speed: '33',
    test_pressings: '0',
    sleeve_package: 'standard',
    inner_sleeve: 'generic',
    outer_protection: 'none',
    printing: 'none',
    spine: '3mm'
  });
  const [previewResult, setPreviewResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // State for price catalogs
  const [priceCatalogs, setPriceCatalogs] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [rulesRes, catalogsRes, optionsRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing/configuration-rules`, { headers }),
        fetch(`${API_URL}/api/pricing/catalogs`, { headers }),
        fetch(`${API_URL}/api/pricing/configurable-options`, { headers })
      ]);
      
      if (!rulesRes.ok) throw new Error('Failed to fetch rules');
      if (!catalogsRes.ok) throw new Error('Failed to fetch price catalogs');
      if (!optionsRes.ok) throw new Error('Failed to fetch configurable options');
      
      // Read each response separately to avoid stream issues
      const rulesData = await rulesRes.json();
      const catalogsData = await catalogsRes.json();
      const optionsData = await optionsRes.json();
      
      setRules(rulesData);
      setPriceCatalogs(catalogsData);
      setConfigurableOptions(optionsData);
      
      // Auto-select first catalog with items (preferably Sony)
      const sonyCatalog = catalogsData.find(c => c.name.toLowerCase().includes('sony'));
      if (sonyCatalog) {
        setSelectedCatalogId(sonyCatalog.id);
        await fetchCatalogItems(sonyCatalog.id, headers);
      } else if (catalogsData.length > 0) {
        setSelectedCatalogId(catalogsData[0].id);
        await fetchCatalogItems(catalogsData[0].id, headers);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch items for a specific catalog
  const fetchCatalogItems = async (catalogId, headers) => {
    if (!catalogId) return;
    try {
      const res = await fetch(`${API_URL}/api/pricing/items?catalog_id=${catalogId}&limit=500`, { 
        headers: headers || { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const text = await res.text();
        const data = JSON.parse(text);
        // Transform to simpler format for the UI
        const items = data.items.map(item => ({
          item_code: item.sku,
          description: item.name,
          category: item.category,
          price_per_unit: item.tiers?.[0]?.cost_price || 0,
          tiers: item.tiers,
          catalog_id: item.catalog_id,
        }));
        setSonyItems(items);
      }
    } catch (err) {
      console.error('Failed to fetch catalog items:', err);
    }
  };

  // Handle catalog change
  const handleCatalogChange = async (catalogId) => {
    setSelectedCatalogId(catalogId);
    await fetchCatalogItems(catalogId);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.option_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.option_value.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterOptionId || filterOptionId === 'all' || rule.option_id === filterOptionId;
    return matchesSearch && matchesFilter;
  });

  // Group rules by option_id for display
  const groupedRules = filteredRules.reduce((acc, rule) => {
    if (!acc[rule.option_id]) {
      acc[rule.option_id] = [];
    }
    acc[rule.option_id].push(rule);
    return acc;
  }, {});

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/configuration-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to delete rule');
      
      toast.success('Rule deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const calculatePreview = async () => {
    setIsCalculating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Map our preview config to the pricing API format
      const config = {
        format: previewConfig.format,
        quantity: parseInt(previewConfig.quantity) || 100,
        color: previewConfig.color_mode === 'color' ? 'Colored' : 'Black',
        vinyl_weight_g: parseInt(previewConfig.vinyl_weight.replace('g', '')) || 140,
        sleeve_type: previewConfig.sleeve_package,
        print_colors: previewConfig.printing,
        test_pressings: parseInt(previewConfig.test_pressings) || 0
      };
      
      const res = await fetch(`${API_URL}/api/pricing/customer/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (!res.ok) throw new Error('Calculation failed');
      
      const result = await res.json();
      setPreviewResult(result);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsCalculating(false);
    }
  };

  const uniqueOptionIds = [...new Set(rules.map(r => r.option_id))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Catalog Selector */}
      <div className="flex items-center gap-4">
        <Label className="text-sm font-medium">Price Catalog:</Label>
        <Select value={selectedCatalogId} onValueChange={handleCatalogChange}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select catalog..." />
          </SelectTrigger>
          <SelectContent>
            {priceCatalogs.map(catalog => (
              <SelectItem key={catalog.id} value={catalog.id}>
                {catalog.name} ({catalog.item_count} items)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedCatalogId && (
          <Badge variant="secondary">
            {sonyItems.length} items loaded
          </Badge>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Link2 className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{rules.length}</p>
                <p className="text-sm text-slate-500">Active Rules</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Check className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{sonyItems.length}</p>
                <p className="text-sm text-slate-500">Catalog SKUs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Filter className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{configurableOptions.length}</p>
                <p className="text-sm text-slate-500">Customer Options</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => setIsPreviewOpen(true)}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calculator className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-600">Preview Calculator</p>
                <p className="text-xs text-slate-500">Test pricing rules →</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Rules List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search rules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterOptionId} onValueChange={setFilterOptionId}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Options</SelectItem>
                {uniqueOptionIds.map(id => (
                  <SelectItem key={id} value={id}>{id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Button>
          </div>

          {/* Rules by Option Group */}
          {Object.keys(groupedRules).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Link2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Configuration Rules</h3>
                <p className="text-slate-500 mb-4">
                  Create rules to map customer options to Sony catalog items.
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedRules).map(([optionId, optionRules]) => (
                <Card key={optionId}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium text-slate-900">
                        {optionId}
                      </CardTitle>
                      <Badge variant="secondary">{optionRules.length} rules</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="divide-y divide-slate-100">
                      {optionRules.map(rule => (
                        <div
                          key={rule.id}
                          className={`py-3 flex items-center justify-between group cursor-pointer hover:bg-background -mx-4 px-4 ${
                            selectedRule?.id === rule.id ? 'bg-indigo-50' : ''
                          }`}
                          onClick={() => setSelectedRule(rule)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-900">
                                = &quot;{rule.option_value}&quot;
                              </span>
                              <span className="text-xs text-slate-500">
                                → {rule.sony_item_codes?.length || 0} items
                                {rule.is_fixed_cost && ' (fixed)'}
                                {rule.quantity_multiplier !== 1 && ` (×${rule.quantity_multiplier})`}
                              </span>
                            </div>
                            {rule.conditions && Object.keys(rule.conditions).length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                +{Object.keys(rule.conditions).length} conditions
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRule(rule.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right: Rule Detail / Editor */}
        <div className="lg:col-span-1">
          {selectedRule ? (
            <RuleDetailPanel
              rule={selectedRule}
              sonyItems={sonyItems}
              configurableOptions={configurableOptions}
              onClose={() => setSelectedRule(null)}
              onUpdate={fetchData}
            />
          ) : (
            <Card className="h-full min-h-[400px]">
              <CardContent className="h-full flex items-center justify-center text-center py-12">
                <div>
                  <Eye className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Select a Rule</h3>
                  <p className="text-sm text-slate-500">
                    Click on a rule to view details and edit
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Rule Modal */}
      <CreateRuleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        sonyItems={sonyItems}
        configurableOptions={configurableOptions}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          // Small delay to ensure modal is fully closed before refetching
          setTimeout(() => fetchData(), 100);
        }}
      />

      {/* Preview Calculator Modal */}
      <PreviewCalculatorModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        configurableOptions={configurableOptions}
        previewConfig={previewConfig}
        setPreviewConfig={setPreviewConfig}
        previewResult={previewResult}
        isCalculating={isCalculating}
        onCalculate={calculatePreview}
      />
    </div>
  );
}

/**
 * Rule Detail Panel - Shows selected rule details and allows editing
 */
function RuleDetailPanel({ rule, sonyItems, configurableOptions, onClose, onUpdate }) {
  const linkedItems = sonyItems.filter(item => rule.sony_item_codes?.includes(item.item_code));
  
  return (
    <Card className="sticky top-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Rule Details</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Rule Identity */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">OPTION</Label>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{rule.option_id}</Badge>
            <span className="text-slate-600">=</span>
            <Badge>{rule.option_value}</Badge>
          </div>
        </div>

        {/* Conditions */}
        {rule.conditions && Object.keys(rule.conditions).length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">CONDITIONS (AND)</Label>
            <div className="space-y-1">
              {Object.entries(rule.conditions).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className="text-slate-600">{key}</span>
                  <span className="text-slate-400">=</span>
                  <span className="font-mono text-indigo-600">{JSON.stringify(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quantity Settings */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">QUANTITY MODE</Label>
            <p className="text-sm font-medium">
              {rule.is_fixed_cost ? 'Fixed (one-time)' : 'Per Unit'}
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">MULTIPLIER</Label>
            <p className="text-sm font-medium">×{rule.quantity_multiplier || 1}</p>
          </div>
        </div>

        {/* Linked Sony Items */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">
            LINKED SONY ITEMS ({linkedItems.length})
          </Label>
          {linkedItems.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {linkedItems.map(item => (
                <div key={item.item_code} className="p-2 bg-background rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-600">{item.item_code}</span>
                    <span className="text-sm font-medium text-emerald-600">
                      €{item.price_per_unit?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{item.description}</p>
                  {item.inch && (
                    <Badge variant="outline" className="text-xs mt-1">{item.inch}&quot;</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-amber-600">
              <AlertCircle className="inline h-4 w-4 mr-1" />
              No Sony items linked
            </p>
          )}
        </div>

        {/* Priority */}
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">PRIORITY</Label>
          <p className="text-sm">{rule.priority || 0} (higher = checked first)</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Create Rule Modal
 */
function CreateRuleModal({ isOpen, onClose, sonyItems, configurableOptions, onSuccess }) {
  const [formData, setFormData] = useState({
    option_id: '',
    option_value: '',
    sony_item_codes: [],
    conditions: {},
    quantity_multiplier: 1,
    is_fixed_cost: false,
    priority: 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchSonyItem, setSearchSonyItem] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Get available values for selected option
  const selectedOption = configurableOptions.find(o => o.option_id === formData.option_id);
  const availableValues = selectedOption?.values || [];

  // Get unique categories from Sony items
  const uniqueCategories = [...new Set(sonyItems.map(i => i.category).filter(Boolean))].sort();

  // Filter Sony items for selection
  const filteredSonyItems = sonyItems.filter(item => {
    const matchesSearch = !searchSonyItem || 
      item.item_code.toLowerCase().includes(searchSonyItem.toLowerCase()) ||
      item.description.toLowerCase().includes(searchSonyItem.toLowerCase());
    const matchesCategory = !filterCategory || filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.option_id || !formData.option_value) {
      setError('Option and value are required');
      return;
    }
    
    if (formData.sony_item_codes.length === 0) {
      setError('Select at least one SKU');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/configuration-rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      // Read response as text first, then parse
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text || 'Unknown error' };
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create rule');
      }
      
      toast.success('Rule created successfully');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSonyItem = (itemCode) => {
    setFormData(prev => ({
      ...prev,
      sony_item_codes: prev.sony_item_codes.includes(itemCode)
        ? prev.sony_item_codes.filter(c => c !== itemCode)
        : [...prev.sony_item_codes, itemCode]
    }));
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        option_id: '',
        option_value: '',
        sony_item_codes: [],
        conditions: {},
        quantity_multiplier: 1,
        is_fixed_cost: false,
        priority: 0
      });
      setError(null);
      setSearchSonyItem('');
      setFilterCategory('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Configuration Rule</DialogTitle>
          <DialogDescription>
            Map a customer option selection to Sony catalog items
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Option Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer Option *</Label>
              <Select
                value={formData.option_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, option_id: value, option_value: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select option..." />
                </SelectTrigger>
                <SelectContent>
                  {configurableOptions.map(opt => (
                    <SelectItem key={opt.option_id} value={opt.option_id}>
                      {opt.label} ({opt.option_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Option Value *</Label>
              <Select
                value={formData.option_value}
                onValueChange={(value) => setFormData(prev => ({ ...prev, option_value: value }))}
                disabled={!formData.option_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select value..." />
                </SelectTrigger>
                <SelectContent>
                  {availableValues.map(val => (
                    <SelectItem key={val.value} value={val.value}>
                      {val.label} ({val.value})
                    </SelectItem>
                  ))}
                  {/* Allow custom values too */}
                  <SelectItem value="__custom__">Custom value...</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            <Label>Conditions (optional)</Label>
            <p className="text-xs text-slate-500">
              Add conditions when this rule should apply (e.g., format = 12)
            </p>
            <ConditionsEditor
              conditions={formData.conditions}
              onChange={(conditions) => setFormData(prev => ({ ...prev, conditions }))}
              configurableOptions={configurableOptions}
            />
          </div>

          {/* Quantity Settings */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Quantity Multiplier</Label>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                value={formData.quantity_multiplier}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity_multiplier: parseFloat(e.target.value) || 1 }))}
              />
              <p className="text-xs text-slate-500">e.g., 0.5 for per pair</p>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
              />
              <p className="text-xs text-slate-500">Higher = checked first</p>
            </div>

            <div className="space-y-2 pt-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_fixed"
                  checked={formData.is_fixed_cost}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_fixed_cost: checked }))}
                />
                <Label htmlFor="is_fixed" className="text-sm">Fixed cost (one-time)</Label>
              </div>
            </div>
          </div>

          {/* Sony Item Selection */}
          <div className="space-y-2">
            <Label>Price Catalog SKUs *</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search SKUs..."
                  value={searchSonyItem}
                  onChange={(e) => setSearchSonyItem(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {uniqueCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {filteredSonyItems.slice(0, 100).map(item => (
                <div
                  key={item.item_code}
                  className={`flex items-center justify-between p-2 hover:bg-background cursor-pointer border-b last:border-b-0 ${
                    formData.sony_item_codes.includes(item.item_code) ? 'bg-indigo-50' : ''
                  }`}
                  onClick={() => toggleSonyItem(item.item_code)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                      formData.sony_item_codes.includes(item.item_code) 
                        ? 'bg-indigo-600 border-indigo-600' 
                        : 'border-slate-300'
                    }`}>
                      {formData.sony_item_codes.includes(item.item_code) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-600">{item.item_code}</span>
                        {item.category && (
                          <Badge variant="outline" className="text-xs">{item.category}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-[280px]">{item.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-emerald-600">€{item.price_per_unit?.toFixed(2)}</span>
                    {item.tiers && item.tiers.length > 1 && (
                      <p className="text-xs text-slate-400">{item.tiers.length} tiers</p>
                    )}
                  </div>
                </div>
              ))}
              {filteredSonyItems.length === 0 && (
                <div className="p-4 text-center text-sm text-slate-500">
                  No items found. Try a different search or category.
                </div>
              )}
              {filteredSonyItems.length > 100 && (
                <div className="p-2 text-center text-xs text-slate-400 bg-background">
                  Showing first 100 of {filteredSonyItems.length} items. Use search to narrow results.
                </div>
              )}
            </div>
            {formData.sony_item_codes.length > 0 && (
              <p className="text-xs text-indigo-600">
                {formData.sony_item_codes.length} SKU(s) selected
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Rule
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Conditions Editor - Simple AND-only conditions builder
 */
function ConditionsEditor({ conditions, onChange, configurableOptions }) {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const addCondition = () => {
    if (newKey && newValue) {
      onChange({ ...conditions, [newKey]: newValue });
      setNewKey('');
      setNewValue('');
    }
  };

  const removeCondition = (key) => {
    const updated = { ...conditions };
    delete updated[key];
    onChange(updated);
  };

  // Get values for selected option
  const selectedOption = configurableOptions.find(o => o.option_id === newKey);
  const availableValues = selectedOption?.values || [];

  return (
    <div className="space-y-2">
      {/* Existing conditions */}
      {Object.entries(conditions).map(([key, value]) => (
        <div key={key} className="flex items-center gap-2 bg-background p-2 rounded">
          <Badge variant="outline">{key}</Badge>
          <span className="text-slate-400">=</span>
          <Badge>{value}</Badge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeCondition(key)}
            className="ml-auto"
          >
            <X className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}

      {/* Add new condition */}
      <div className="flex gap-2">
        <Select value={newKey} onValueChange={setNewKey}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Option..." />
          </SelectTrigger>
          <SelectContent>
            {configurableOptions.map(opt => (
              <SelectItem key={opt.option_id} value={opt.option_id}>
                {opt.option_id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="self-center text-slate-400">=</span>
        
        <Select value={newValue} onValueChange={setNewValue} disabled={!newKey}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Value..." />
          </SelectTrigger>
          <SelectContent>
            {availableValues.map(val => (
              <SelectItem key={val.value} value={val.value}>
                {val.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button type="button" variant="outline" size="sm" onClick={addCondition} disabled={!newKey || !newValue}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Preview Calculator Modal
 */
function PreviewCalculatorModal({
  isOpen,
  onClose,
  configurableOptions,
  previewConfig,
  setPreviewConfig,
  previewResult,
  isCalculating,
  onCalculate
}) {
  // Group options by category
  const vinylOptions = configurableOptions.filter(o => o.category === 'vinyl');
  const sleeveOptions = configurableOptions.filter(o => o.category === 'sleeve');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-purple-600" />
            Preview Calculator
          </DialogTitle>
          <DialogDescription>
            Test pricing rules with a sample configuration
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Left: Configuration */}
          <div className="space-y-6">
            {/* Vinyl Options */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-900 uppercase tracking-wide">Vinyl</h3>
              {vinylOptions.map(opt => (
                <div key={opt.option_id} className="space-y-1">
                  <Label className="text-xs">{opt.label}</Label>
                  {opt.input_type === 'number' ? (
                    <Input
                      type="number"
                      value={previewConfig[opt.option_id] || ''}
                      onChange={(e) => setPreviewConfig(prev => ({ ...prev, [opt.option_id]: e.target.value }))}
                    />
                  ) : (
                    <Select
                      value={previewConfig[opt.option_id] || ''}
                      onValueChange={(value) => setPreviewConfig(prev => ({ ...prev, [opt.option_id]: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {opt.values?.map(val => (
                          <SelectItem key={val.value} value={val.value}>
                            {val.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>

            {/* Sleeve Options */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-900 uppercase tracking-wide">Sleeve</h3>
              {sleeveOptions.map(opt => (
                <div key={opt.option_id} className="space-y-1">
                  <Label className="text-xs">{opt.label}</Label>
                  <Select
                    value={previewConfig[opt.option_id] || ''}
                    onValueChange={(value) => setPreviewConfig(prev => ({ ...prev, [opt.option_id]: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {opt.values?.map(val => (
                        <SelectItem key={val.value} value={val.value}>
                          {val.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <Button onClick={onCalculate} disabled={isCalculating} className="w-full">
              {isCalculating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Calculate Price
                </>
              )}
            </Button>
          </div>

          {/* Right: Results */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-900 uppercase tracking-wide">Results</h3>
            
            {previewResult ? (
              <div className="space-y-4">
                {/* Summary */}
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-sm text-emerald-700">Total Estimate</p>
                      <p className="text-3xl font-bold text-emerald-800">
                        €{previewResult.total_estimate?.toFixed(2)}
                      </p>
                      <p className="text-sm text-emerald-600">
                        (€{previewResult.unit_price_estimate?.toFixed(2)} per unit)
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Line Items (Admin Breakdown) */}
                {previewResult.line_items && previewResult.line_items.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Breakdown</p>
                    <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                      {previewResult.line_items.map((item, idx) => (
                        <div key={idx} className="p-2 flex justify-between text-sm">
                          <div>
                            <span className="font-mono text-xs text-slate-500">{item.sony_item_code}</span>
                            <p className="text-xs text-slate-600 truncate max-w-[200px]">{item.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">€{item.line_total?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">
                              {item.quantity} × €{item.unit_price?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Using fallback pricing. No configuration rules matched.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Config Summary */}
                <div className="text-xs text-slate-500">
                  <p>Calculated at: {new Date(previewResult.calculated_at).toLocaleString()}</p>
                  {previewResult.is_estimate && <p className="text-amber-600">* This is an estimate</p>}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Calculator className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Click Calculate Price to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
